// Imports
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

// UI Imports
import { Box, CircularProgress, Fade, Typography } from '@mui/material'
import { withStyles } from '@material-ui/core/styles'

// App Imports
import { detail } from '../../setting/api/actions/query'
import clientSettings from '../../../setup/config/clientSettings'
import styles from './styles'

// Static cache to avoid redundant API calls
let vendorSettingsCache = {
  data: null,
  loading: false,
  promise: null,
}

// Component
class BrandedLoading extends Component {
  state = {
    clientName: null,
    loading: true,
    showText: false,
  }

  componentDidMount() {
    this.fetchSettings()
    // Only show loading text if request takes more than 600ms
    this.textTimer = setTimeout(() => {
      this.setState({ showText: true })
    }, 600)
  }

  componentWillUnmount() {
    // Clear timer to prevent memory leaks
    if (this.textTimer) {
      clearTimeout(this.textTimer)
    }
  }

  fetchSettings = async () => {
    // Use cached data if available
    if (vendorSettingsCache.data) {
      this.setState({ 
        clientName: vendorSettingsCache.data, 
        loading: false 
      })
      return
    }

    // If already loading, wait for the existing promise
    if (vendorSettingsCache.loading && vendorSettingsCache.promise) {
      try {
        const vendorName = await vendorSettingsCache.promise
        this.setState({ clientName: vendorName, loading: false })
      } catch (error) {
        this.setState({ loading: false })
      }
      return
    }

    // Start new fetch
    vendorSettingsCache.loading = true
    const { detail } = this.props

    vendorSettingsCache.promise = (async () => {
      try {
        const response = await detail({ vendorId: '' })
        const vendorName = response?.data?.data?.vendor?.name

        if (vendorName && clientSettings[vendorName]) {
          vendorSettingsCache.data = vendorName
          vendorSettingsCache.loading = false
          this.setState({ clientName: vendorName, loading: false })
          return vendorName
        } else {
          console.warn('Vendor name not found in clientSettings:', vendorName)
          vendorSettingsCache.loading = false
          this.setState({ loading: false })
          return null
        }
      } catch (error) {
        console.error('Error fetching setting data:', error)
        vendorSettingsCache.loading = false
        this.setState({ loading: false })
        throw error
      }
    })()

    await vendorSettingsCache.promise
  }

  render() {
    const { classes, size = 80 } = this.props
    const { clientName, showText } = this.state
    const client = clientSettings[clientName] || {}
    const favicon = (client.favicon && client.favicon.trim() !== '') ? client.favicon : '/datumart-favicon.ico'
    const hasFavicon = true
    return (
      <Fade in={true} timeout={600}>
        <Box
          className={classes.root}
          role="status"
          aria-busy="true"
          aria-live="polite"
          aria-label="Loading content, please wait"
        >
          {/* Logo with Circular Progress - Only show if favicon exists */}
          {hasFavicon && (
            <Box
              sx={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
              }}
            >
              {/* Circular Progress Indicator - Subtle */}
              <CircularProgress
                size={size + 20}
                thickness={2}
                sx={{
                  color: '#0d3559',
                  position: 'absolute',
                  opacity: 0.6,
                }}
              />

              {/* Logo Container */}
              <Box
                sx={{
                  width: size,
                  height: size,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#fff',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  animation: 'fadeInPulse 1.5s ease-in-out',
                  '@keyframes fadeInPulse': {
                    '0%': {
                      opacity: 0,
                      transform: 'scale(0.9)',
                    },
                    '50%': {
                      opacity: 1,
                      transform: 'scale(1.02)',
                    },
                    '100%': {
                      opacity: 1,
                      transform: 'scale(1)',
                    },
                  },
                }}
              >
                <img
                  src={favicon}
                  alt="Brand logo"
                  style={{
                    width: '70%',
                    height: '70%',
                    objectFit: 'contain',
                  }}
                />
              </Box>
            </Box>
          )}

          {/* Loading Text - Centered Below Logo or Standalone - Only show after 600ms */}
          {showText && (
            <Box
              sx={{
                textAlign: 'center',
                animation: 'fadeInText 1s ease-in-out 0.3s both',
                '@keyframes fadeInText': {
                  '0%': {
                    opacity: 0,
                    transform: 'translateY(10px)',
                  },
                  '100%': {
                    opacity: 1,
                    transform: 'translateY(0)',
                  },
                },
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: '#0d3559',
                  fontWeight: 600,
                  fontSize: '1rem',
                  letterSpacing: '0.03em',
                }}
              >
                Loading…
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#0d3559',
                  fontWeight: 400,
                  fontSize: '0.875rem',
                  mt: 0.5,
                }}
              >
                Thank you for your patience
              </Typography>
            </Box>
          )}
        </Box>
      </Fade>
    )
  }
}

// Component Properties
BrandedLoading.propTypes = {
  classes: PropTypes.object.isRequired,
  size: PropTypes.number,
  detail: PropTypes.func.isRequired,
}

// Component State
const mapDispatchToProps = {
  detail,
}

export default connect(null, mapDispatchToProps)(withStyles(styles)(BrandedLoading))
